'use client';

import * as React from 'react';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface MotionConfig {
  durations: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };
  easings: {
    standard: [number, number, number, number];
    entrance: [number, number, number, number];
    exit: [number, number, number, number];
  };
  reduced: boolean;
}

const defaultConfig: MotionConfig = {
  durations: {
    xs: 150,
    sm: 200,
    md: 220,
    lg: 250,
  },
  easings: {
    standard: [0.4, 0, 0.2, 1],
    entrance: [0.4, 0, 0.2, 1],
    exit: [0.4, 0, 0.2, 1],
  },
  reduced: false,
};

const MotionContext = React.createContext<MotionConfig>(defaultConfig);

export function MotionProvider({ children }: { children: React.ReactNode }) {
  const reducedMotion = useReducedMotion();

  const config: MotionConfig = React.useMemo(() => ({
    ...defaultConfig,
    reduced: reducedMotion,
    // Reduce durations when reduced motion is preferred
    durations: reducedMotion
      ? {
          xs: 0,
          sm: 0,
          md: 150,
          lg: 150,
        }
      : defaultConfig.durations,
  }), [reducedMotion]);

  return (
    <MotionContext.Provider value={config}>
      <div 
        data-motion-reduced={reducedMotion ? 'true' : 'false'}
        style={{
          ['--motion-reduced' as any]: reducedMotion ? '1' : '0',
        }}
      >
        {children}
      </div>
    </MotionContext.Provider>
  );
}

export function useMotion() {
  return React.useContext(MotionContext);
}

/**
 * Hook to get motion-aware animation props
 * Returns safe animation values that respect user preferences
 */
export function useResponsiveMotion() {
  const motion = useMotion();

  return React.useMemo(() => ({
    // For non-essential animations (decorative)
    decorative: motion.reduced 
      ? { duration: 0, transition: 'none' }
      : { 
          duration: motion.durations.sm / 1000, 
          ease: motion.easings.standard,
        },

    // For functional animations (drawer open/close, etc.)
    functional: motion.reduced
      ? { duration: motion.durations.md / 1000, ease: 'ease' }
      : { 
          duration: motion.durations.md / 1000, 
          ease: motion.easings.standard,
        },

    // For focus/interaction feedback (always enabled for accessibility)
    interaction: {
      duration: motion.durations.xs / 1000,
      ease: motion.easings.standard,
    },

    // CSS custom properties for use in stylesheets
    cssVars: {
      '--motion-duration-xs': `${motion.durations.xs}ms`,
      '--motion-duration-sm': `${motion.durations.sm}ms`,
      '--motion-duration-md': `${motion.durations.md}ms`,
      '--motion-duration-lg': `${motion.durations.lg}ms`,
      '--motion-ease-standard': `cubic-bezier(${motion.easings.standard.join(', ')})`,
      '--motion-reduced': motion.reduced ? '1' : '0',
    },
  }), [motion]);
}